import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiInbox, FiSearch, FiFlag, FiCheckCircle, FiSlash,
  FiMessageCircle, FiAward, FiArrowRight, FiClock, FiArchive,
} from 'react-icons/fi';

import { Spinner } from '../../components/ui/Spinner';
import { Alert } from '../../components/ui/Alert';
import { PortalPage } from '../../components/layout/PortalPage';
import { SectionCard } from '../../components/layout/SectionCard';
import { getEstabelecimentoAtualId } from '../../lib/estabelecimento';
import {
  protocolosLegislativosService, TIPO_PL_LABEL, TIPOS_PL_LISTA,
  DECISAO_LABEL, type Decisao, type PLListaItem, type ResumoPL,
} from '../../services/protocoloLegislativo.service';
import { formatarDataBR } from '../../lib/data';

export function ProtocoloLegislativoPublicoPage() {
  const anoAtual = new Date().getFullYear();
  const [ano, setAno] = useState(anoAtual);
  const [tipo, setTipo] = useState('');
  const [decisao, setDecisao] = useState('');
  const [busca, setBusca] = useState('');
  const [buscaDebounced, setBuscaDebounced] = useState('');
  const [protocolos, setProtocolos] = useState<PLListaItem[]>([]);
  const [resumo, setResumo] = useState<ResumoPL | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    const id = setTimeout(() => setBuscaDebounced(busca), 300);
    return () => clearTimeout(id);
  }, [busca]);

  useEffect(() => {
    setCarregando(true);
    setErro(null);
    const id = getEstabelecimentoAtualId();
    protocolosLegislativosService.listar(id, ano, tipo || undefined, decisao || undefined, undefined, buscaDebounced || undefined)
      .then((r) => { setProtocolos(r.protocolos); setResumo(r.resumo); })
      .catch((e) => setErro(e instanceof Error ? e.message : 'Erro'))
      .finally(() => setCarregando(false));
  }, [ano, tipo, decisao, buscaDebounced]);

  const opcoesAno = [anoAtual, anoAtual - 1, anoAtual - 2, anoAtual - 3, anoAtual - 4];

  return (
    <PortalPage
      titulo="Protocolo legislativo"
      subtitulo="Projetos, requerimentos e indicações vindos da Câmara Municipal."
      icone={<FiArchive size={26} />}
      largura="larga"
      voltarHref="/"
      voltarLabel="Voltar ao portal"
      breadcrumbs={[
        { label: 'Transparência' },
        { label: 'Protocolo legislativo' },
      ]}
    >
      <div className="space-y-5">
        {/* Resumo do ano */}
        {resumo && resumo.total > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KPI cor="slate" icone={<FiClock size={14} />} label="Total" valor={resumo.total} />
            <KPI cor="amber" icone={<FiClock size={14} />} label="Pendentes" valor={resumo.pendentes} />
            <KPI cor="emerald" icone={<FiCheckCircle size={14} />} label="Sancionados" valor={resumo.sancionados} />
            <KPI cor="emerald" icone={<FiAward size={14} />} label="Promulgados" valor={resumo.promulgados} />
            <KPI cor="red" icone={<FiSlash size={14} />} label="Vetados" valor={resumo.vetados} />
            <KPI cor="blue" icone={<FiMessageCircle size={14} />} label="Respondidos" valor={resumo.devolvidos} />
          </div>
        )}

        {/* Filtros */}
        <SectionCard titulo="Filtros" icone={<FiSearch size={18} />}>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm font-medium"
            >
              {opcoesAno.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
            >
              <option value="">Todos os tipos</option>
              {TIPOS_PL_LISTA.map((t) => (
                <option key={t} value={t}>{TIPO_PL_LABEL[t]}</option>
              ))}
            </select>
            <select
              value={decisao}
              onChange={(e) => setDecisao(e.target.value)}
              className="h-9 px-3 rounded-md border border-slate-300 bg-white text-sm"
            >
              <option value="">Todas as decisões</option>
              <option value="pendente">Aguardando decisão</option>
              <option value="sancionado">Sancionados</option>
              <option value="promulgado">Promulgados</option>
              <option value="vetado">Vetados</option>
              <option value="devolvido">Respondidos</option>
            </select>
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por número, ementa ou título..."
                className="w-full h-9 pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm"
              />
            </div>
          </div>
        </SectionCard>

        {carregando ? (
          <div className="flex justify-center py-16"><Spinner tamanho="lg" /></div>
        ) : erro ? (
          <Alert variante="erro">{erro}</Alert>
        ) : protocolos.length === 0 ? (
          <SectionCard>
            <div className="text-center py-12">
              <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FiInbox className="text-slate-400" size={26} />
              </div>
              <p className="text-sm font-medium text-slate-700">
                {busca || tipo || decisao
                  ? 'Nenhum protocolo encontrado com os filtros atuais.'
                  : `Nenhum protocolo legislativo registrado em ${ano}.`}
              </p>
            </div>
          </SectionCard>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-slate-500">
              {protocolos.length} {protocolos.length === 1 ? 'protocolo encontrado' : 'protocolos encontrados'}
            </div>
            {protocolos.map((p) => (
              <Link
                key={p.id}
                to={`/transparencia/protocolo-legislativo/${p.id}`}
                className="block bg-white border border-slate-200 rounded-xl hover:border-slate-400 hover:shadow-sm transition"
              >
                <div className="flex items-start gap-4 p-4">
                  <div className="flex-shrink-0 mt-0.5">
                    <DecisaoIcone decisao={p.decisao} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900">
                        {p.tipo_pl ? TIPO_PL_LABEL[p.tipo_pl] : 'Protocolo'}
                        {p.numero_camara && <> nº {p.numero_camara}</>}
                      </span>
                      <DecisaoBadgePortal decisao={p.decisao} />
                      {p.ato_oficial_numero && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                          Ato {p.ato_oficial_numero}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-700 mt-1 line-clamp-2">
                      {p.ementa || p.titulo}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-500">
                      {p.autor_camara && <span>Autor: <strong>{p.autor_camara}</strong></span>}
                      <span>· Recebido em {formatarDataBR(p.data_recebimento_executivo)}</span>
                      {p.data_decisao && <span>· Decidido em {formatarDataBR(p.data_decisao)}</span>}
                    </div>
                  </div>
                  <FiArrowRight className="text-slate-300 flex-shrink-0 mt-1" size={16} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </PortalPage>
  );
}

const COR_KPI: Record<string, string> = {
  slate: 'border-slate-200 bg-white text-slate-600',
  amber: 'border-amber-200 bg-amber-50 text-amber-800',
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  red: 'border-red-200 bg-red-50 text-red-800',
  blue: 'border-blue-200 bg-blue-50 text-blue-800',
};

function KPI({ cor, icone, label, valor }: { cor: string; icone: React.ReactNode; label: string; valor: number }) {
  return (
    <div className={`border rounded-xl p-3 ${COR_KPI[cor]}`}>
      <div className="flex items-center gap-1 text-xs uppercase tracking-wider opacity-80">
        {icone} {label}
      </div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{valor}</div>
    </div>
  );
}

function DecisaoIcone({ decisao }: { decisao: Decisao }) {
  switch (decisao) {
    case 'sancionado':
    case 'promulgado':
      return <FiCheckCircle className="text-emerald-500" size={18} />;
    case 'vetado':
      return <FiSlash className="text-red-500" size={18} />;
    case 'devolvido':
      return <FiMessageCircle className="text-blue-500" size={18} />;
    default:
      return <FiFlag className="text-slate-400" size={18} />;
  }
}

function DecisaoBadgePortal({ decisao }: { decisao: Decisao }) {
  const cores: Record<Decisao, string> = {
    pendente: 'bg-amber-100 text-amber-800',
    sancionado: 'bg-emerald-100 text-emerald-800',
    promulgado: 'bg-emerald-100 text-emerald-800',
    vetado: 'bg-red-100 text-red-800',
    devolvido: 'bg-blue-100 text-blue-800',
    arquivado: 'bg-slate-200 text-slate-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${cores[decisao]}`}>
      {DECISAO_LABEL[decisao]}
    </span>
  );
}
